"use client";

import { useContext } from "react";
import { useDroppable } from "@dnd-kit/core";
import { useCombinedRefs } from "@dnd-kit/utilities";
import { Copy, Pencil, Settings, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { LomdaV3Block } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { BlockEditableProvider } from "../../../hooks/useBlockEditable";
import { SlotDepthContext, useBlockDnd } from "../../../hooks/useBlockDnd";
import { useRevealBlock } from "../../../hooks/useRevealBlock";
import { isBlockEmpty } from "../../../lib/blockRegistry";
import {
    blockLabelKey,
    isInlineBlock,
} from "../../../lib/sectionRegistry";
import { BlockList } from "../../../lib/types";
import { DragData } from "../../../lib/dropTarget";
import BlockView from "../blockView";
import BlockDragHandle from "../dnd/blockDragHandle";
import { DropHereBox, DropLine, SlotHighlight } from "../dnd/dropIndicators";
import { NESTED_GAP_PX } from "../../../lib/constants";


/**
 * Renders a container slot's child blocks. Preview shows them read-only; the
 * editor adds per-block controls + an "add block" menu (any block can nest).
 */
export default function NestedBlocks({
    containerId,
    slotId,
    blocks,
    editable,
    previewGap = "gap-6",
}: {
    containerId: string;
    slotId: string;
    blocks: LomdaV3Block[];
    editable: boolean;
    previewGap?: string;
}) {
    if (!editable) {
        return (
            <div className={cn("flex flex-col", previewGap)}>
                {blocks.map((b) => (
                    <BlockEditableProvider key={b.id} editable={false}>
                        <BlockView block={b} />
                    </BlockEditableProvider>
                ))}
            </div>
        );
    }
    return (
        <EditableSlot containerId={containerId} slotId={slotId} blocks={blocks} />
    );
}

// Only editable slots register: read-only page thumbnails share the DndContext,
// and a droppable with the same id would replace the canvas one.
function EditableSlot({
    containerId,
    slotId,
    blocks,
}: {
    containerId: string;
    slotId: string;
    blocks: LomdaV3Block[];
}) {
    const depth = useContext(SlotDepthContext) + 1;
    const list: BlockList = { containerId, slotId };
    const data: DragData = { kind: "slot", containerId, slotId, depth };
    const { setNodeRef } = useDroppable({
        id: `slot:${containerId}:${slotId}`,
        data,
    });

    return (
        <SlotDepthContext.Provider value={depth}>
            <div ref={setNodeRef} className="relative flex flex-col gap-2">
                <SlotHighlight list={list} />
                {blocks.map((b, i) => (
                    <NestedBlock key={b.id} block={b} list={list} index={i} />
                ))}
                <DropHereBox
                    list={list}
                    count={blocks.length}
                    className="rounded-lg py-5"
                />
            </div>
        </SlotDepthContext.Provider>
    );
}

/**
 * A nested text block sits inside a small frame — the wrapper's padding plus its
 * selection ring — and a click landing on that frame missed the editor. The
 * topbar reads "no editor focused" and falls back to the block's own settings,
 * so the author who clicked their paragraph gets width and background controls
 * instead of the text formatting tools. Hand those clicks to the nearest editor.
 */
function focusNearestText(
    block: LomdaV3Block,
    e: React.PointerEvent<HTMLDivElement>
) {
    if (block.type !== "text") return;
    const target = e.target as HTMLElement;
    // React bubbles events out of portals: a click in this block's own popover.
    if (!e.currentTarget.contains(target)) return;
    // Already on the text, or on the block's own hover controls.
    if (target.closest(".lomda-v3-rich-text") || target.closest("button")) {
        return;
    }

    // The class sits on the editable element itself (TipTap sets it as the
    // editor's own class attribute), not on a wrapper around it.
    const editors = Array.from(
        e.currentTarget.querySelectorAll<HTMLElement>(".lomda-v3-rich-text")
    );
    if (editors.length === 0) return;

    // Nearest by distance to the click, so a multi-field block hands over the
    // field the author was actually aiming at.
    const { clientX: x, clientY: y } = e;
    const nearest = editors.reduce((best, el) => {
        const distance = (node: HTMLElement) => {
            const r = node.getBoundingClientRect();
            const dx = Math.max(r.left - x, 0, x - r.right);
            const dy = Math.max(r.top - y, 0, y - r.bottom);
            return dx * dx + dy * dy;
        };
        return distance(el) < distance(best) ? el : best;
    });
    // Without this the browser's own pointer-down focus lands on the frame we
    // just clicked and takes the caret straight back off the editor.
    e.preventDefault();
    nearest.focus();
}

function NestedBlock({
    block,
    list,
    index,
}: {
    block: LomdaV3Block;
    list: BlockList;
    index: number;
}) {
    const selectedBlockId = useLomdaV3Editor((s) => s.selectedBlockId);
    const setSelectedBlockId = useLomdaV3Editor((s) => s.setSelectedBlockId);
    const showBlockSettings = useLomdaV3Editor((s) => s.showBlockSettings);
    const duplicateBlock = useLomdaV3Editor((s) => s.duplicateBlock);
    const deleteBlock = useLomdaV3Editor((s) => s.deleteBlock);
    const openBlockEditor = useLomdaV3Editor((s) => s.openBlockEditor);

    const { setNodeRef, handle, isDragging } = useBlockDnd(block, list, index);
    const ref = useCombinedRefs(setNodeRef, useRevealBlock(block.id));

    const isSelected = selectedBlockId === block.id;
    const inline = isInlineBlock(block.type);
    const empty = isBlockEmpty(block);

    return (
        <div
            ref={ref}
            onPointerDownCapture={() => setSelectedBlockId(block.id)}
            onPointerDown={(e) => {
                e.stopPropagation();
                focusNearestText(block, e);
            }}
            className={cn(
                "group/nested relative scroll-mt-6 rounded-none p-1.5 transition",
                // Platform colour, not `ring-primary`: inside the canvas
                // `--primary` is the LOMDA theme's, so selection chrome would
                // change colour with the lesson.
                isSelected
                    ? "ring-2 ring-[hsl(var(--platform-primary))]"
                    : "hover:ring-2 hover:ring-[hsl(var(--platform-primary)/0.5)]",
                isDragging && "opacity-40"
            )}
        >
            <DropLine list={list} index={index} gap={NESTED_GAP_PX} />
            <div className="lomda-v3-card-text absolute start-1.5 top-1.5 z-20 flex items-center gap-0.5 rounded border bg-card p-0.5 opacity-0 shadow-sm transition group-hover/nested:opacity-100">
                <BlockDragHandle {...handle} />
                <button
                    type="button"
                    aria-label={t("lomda-v3-editor.block.settings")}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => showBlockSettings(block.id)}
                    className="flex size-6 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                    <Settings className="size-3.5" />
                </button>
                <button
                    type="button"
                    aria-label={t("lomda-v3-editor.block.duplicate")}
                    onClick={() => duplicateBlock(block.id)}
                    className="flex size-6 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                    <Copy className="size-3.5" />
                </button>
                <button
                    type="button"
                    aria-label={t("lomda-v3-editor.block.delete")}
                    onClick={() => deleteBlock(block.id)}
                    className="flex size-6 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-destructive"
                >
                    <Trash2 className="size-3.5" />
                </button>
            </div>

            <BlockEditableProvider editable={inline}>
                {inline ? (
                    <BlockView block={block} />
                ) : empty ? (
                    <button
                        type="button"
                        onClick={() => openBlockEditor(block.id)}
                        className="lomda-v3-chrome flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-6 text-sm text-muted-foreground transition hover:border-primary hover:text-primary"
                    >
                        <Pencil className="size-4" />
                        {t("lomda-v3-editor.block.empty-edit", undefined, {
                            block: t(blockLabelKey(block)),
                        })}
                    </button>
                ) : (
                    <div
                        className="relative"
                        onDoubleClick={() => openBlockEditor(block.id)}
                    >
                        <BlockView block={block} />
                        <button
                            type="button"
                            onClick={() => openBlockEditor(block.id)}
                            className="lomda-v3-chrome absolute end-2 top-2 z-10 flex items-center gap-1 rounded-full border bg-card px-2.5 py-1 text-sm text-muted-foreground opacity-0 shadow-sm transition hover:text-primary group-hover/nested:opacity-100"
                        >
                            <Pencil className="size-3.5" />
                            {t("lomda-v3-editor.block.edit")}
                        </button>
                    </div>
                )}
            </BlockEditableProvider>
        </div>
    );
}
